import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { deleteLeadsWithCalendarCleanup, DeletedLeadData, restoreGanttItemsWithCalendarSync } from "@/lib/deleteLeadWithCalendarCleanup";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Link2, Copy, Download, Settings, Undo, History, Clock, Trash2, CheckSquare, CalendarSync, Loader2, Lock, Calendar, X } from "lucide-react";
import { useGoogleCalendarSync } from "@/hooks/useGoogleCalendarSync";
import { calendarDatesFromPosition, calendarDatesFromSelection } from "@/lib/calendarDates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LeadFilters from "@/components/LeadFilters";
import LeadsTable from "@/components/LeadsTable";
import AddLeadDialog from "@/components/AddLeadDialog";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { toast as sonnerToast } from "sonner";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePermissionRedirect } from "@/hooks/usePermissionRedirect";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { exportLeadsToCSV } from "@/lib/exportCsv";
import GroupSettingsDialog from "@/components/GroupSettingsDialog";
import { LeadCountBadge } from "@/components/LeadCountBadge";
import { VersionHistoryDialog } from "@/components/VersionHistoryDialog";
import { CreateGroupDialog } from "@/components/CreateGroupDialog";

import { z } from "zod";

import { ColumnConfig, ensureDefaults } from "@/lib/columnConfig";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

interface Lead {
  id: string;
  lead_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  priority: string;
  value: number | null;
  source: string | null;
  conversation_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
  custom_fields?: Record<string, any>;
  last_refreshed_at?: string | null;
  user_id?: string;
  group_id?: string | null;
}

interface OutletContextType {
  highlightedLeadId: string | null;
  openLeadDetails: string | null;
  setOpenLeadDetails: (id: string | null) => void;
}

const Refresher = () => {
  
  const navigate = useNavigate();
  const { workspace, selectedGroupId, isGroupSwitching } = useWorkspace();
  const { checkPersonalAccess } = usePermissionRedirect();
  const outletContext = useOutletContext<OutletContextType>();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([]);
  const [reminderDays, setReminderDays] = useState<number>(30);
  const [groupName, setGroupName] = useState<string>("");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [lastChange, setLastChange] = useState<{ type: 'update' | 'delete', lead: Lead, deletedData?: DeletedLeadData } | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [customReminderDays, setCustomReminderDays] = useState<Record<string, string>>({});
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [bulkCustomDays, setBulkCustomDays] = useState<string>("");
  const leadRequestIdRef = useRef(0);
  

  // Google Calendar sync for the whole sheet
  const workspaceInfo = {
    workspaceType: workspace === 'groups' ? 'group' as const : 'personal' as const,
    groupId: workspace === 'groups' ? selectedGroupId : null,
    workspaceName: workspace === 'groups' ? (groupName || 'Group') : 'Personal',
  };
  const {
    isConnected: isCalendarConnected,
    isLoading: isCalendarLoading,
    isSyncing: isCalendarSyncing,
    canAccessCalendarSync,
    connect: connectCalendar,
    disconnect: disconnectCalendar,
  } = useGoogleCalendarSync(workspaceInfo);

  const handleSyncAllLeads = async () => {
    if (!isCalendarConnected || !canAccessCalendarSync) return;

    const isGroupWorkspace = workspace === 'groups' && !!selectedGroupId;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const leadIds = leads.map(l => l.id);
      if (leadIds.length === 0) {
        toast("No leads", { description: "There are no leads to sync." });
        return;
      }
      const { data: ganttItems, error } = await supabase
        .from("lead_gantt_items")
        .select("*")
        .in("lead_id", leadIds);
      if (error) throw error;
      if (!ganttItems || ganttItems.length === 0) {
        toast("Nothing to sync", { description: "No timeline tasks found for these leads." });
        return;
      }

      const browserTimezone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();

      let syncedCount = 0;
      for (const item of ganttItems) {
        const lead = leads.find(l => l.id === item.lead_id);

        const dates = item.start_date
          ? calendarDatesFromSelection(item.start_date, item.width / 80)
          : calendarDatesFromPosition(item.start_position, item.width);
        const startDate = item.start_date ?? dates.start_date;
        const endDate = item.end_date ?? dates.end_date;

        // Group workspaces: push to ALL members' calendars via sync-group.
        // Personal workspaces: push only to the current user's calendar via sync-item.
        const endpoint = isGroupWorkspace
          ? 'google-calendar-sync/sync-group'
          : 'google-calendar-sync/sync-item';

        const response = await supabase.functions.invoke(endpoint, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: { item, leadName: lead?.lead_name || 'Lead', leadId: item.lead_id, startDate, endDate, timezone: browserTimezone, workspaceType: workspaceInfo.workspaceType, groupId: workspaceInfo.groupId, workspaceName: workspaceInfo.workspaceName, action: 'upsert' },
        });
        if (response.data?.success) syncedCount++;
      }

      toast.success("Calendar synced", {
        description: isGroupWorkspace
          ? `${syncedCount} task(s) pushed to all group members' calendars.`
          : `${syncedCount} timeline task(s) synced to Google Calendar.`,
      });
    } catch (error: any) {
      toast.error("Sync failed", { description: error.message });
    }
  };

  const customReminderSchema = z.object({
    days: z.number().int().min(1).max(365)
  });

  useEffect(() => {
    // Check permission for personal workspace
    if (workspace === "personal" && !checkPersonalAccess()) {
      return;
    }

    fetchColumnConfig();
    fetchRefresherLeads();
    if (workspace === "groups" && selectedGroupId) {
      fetchGroupInfo();
      fetchInviteLink();
    }

    const handleSessionReady = () => fetchRefresherLeads();
    window.addEventListener('auth-session-ready', handleSessionReady);

    // Set up real-time subscription for leads
    const leadsChannel = supabase
      .channel('refresher-leads-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leads',
          filter: workspace === "groups" && selectedGroupId 
            ? `group_id=eq.${selectedGroupId}` 
            : 'group_id=is.null'
        },
        () => {
          fetchRefresherLeads();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'leads',
          filter: workspace === "groups" && selectedGroupId 
            ? `group_id=eq.${selectedGroupId}` 
            : 'group_id=is.null'
        },
        () => {
          // Re-fetch to re-evaluate filter criteria
          // This ensures leads are removed from Refresher when timer is updated
          fetchRefresherLeads();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'leads',
          filter: workspace === "groups" && selectedGroupId 
            ? `group_id=eq.${selectedGroupId}` 
            : 'group_id=is.null'
        },
        (payload) => {
          // Remove the lead from local state
          setLeads(prev => prev.filter(lead => lead.id !== payload.old.id));
          setFilteredLeads(prev => prev.filter(lead => lead.id !== payload.old.id));
        }
      )
      .subscribe();

    // Set up real-time subscription for group settings (column config, reminder days)
    let groupSettingsChannel;
    if (workspace === "groups" && selectedGroupId) {
      groupSettingsChannel = supabase
        .channel('group-settings-refresher-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'groups',
            filter: `id=eq.${selectedGroupId}`
          },
          () => {
            fetchColumnConfig();
            fetchGroupInfo();
          }
        )
        .subscribe();
    }

    return () => {
      window.removeEventListener('auth-session-ready', handleSessionReady);
      supabase.removeChannel(leadsChannel);
      if (groupSettingsChannel) {
        supabase.removeChannel(groupSettingsChannel);
      }
    };
  }, [workspace, selectedGroupId]);

  const fetchColumnConfig = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      if (workspace === "groups" && selectedGroupId) {
        // Load from groups table
        const { data, error } = await requestWithAuthRecovery(() => supabase
          .from("groups")
          .select("column_config, reminder_days")
          .eq("id", selectedGroupId)
          .single());

        if (error) throw error;

        const config = Array.isArray(data?.column_config)
          ? ensureDefaults(data.column_config as unknown as ColumnConfig[])
          : ensureDefaults([]);
        setColumnConfig(config);
        setReminderDays(data?.reminder_days || 30);
      } else {
        // Load from profiles table
        const { data, error } = await requestWithAuthRecovery(() => supabase
          .from("profiles")
          .select("personal_column_config, reminder_days")
          .eq("user_id", user.id)
          .single());

        if (error) throw error;

        const config = Array.isArray(data?.personal_column_config)
          ? ensureDefaults(data.personal_column_config as unknown as ColumnConfig[])
          : ensureDefaults([]);
        setColumnConfig(config);
        setReminderDays(data?.reminder_days || 30);
      }
    } catch (error: any) {
      console.error("Error fetching column config:", error);
    }
  };

  const fetchGroupInfo = async () => {
    if (!selectedGroupId) return;
    
    const { data } = await requestWithAuthRecovery(() => supabase
      .from("groups")
      .select("name")
      .eq("id", selectedGroupId)
      .single());

    if (data) {
      setGroupName(data.name);
    }
  };

  const fetchInviteLink = async () => {
    if (!selectedGroupId) {
      setInviteLink(null);
      return;
    }
    
    const { data } = await supabase
      .from("group_invites")
      .select("invite_code")
      .eq("group_id", selectedGroupId)
      .is("used_at", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data) {
      const link = `${window.location.origin}/join/${data.invite_code}`;
      setInviteLink(link);
    } else {
      setInviteLink(null);
    }
  };

  const generateInviteLink = async () => {
    if (!selectedGroupId) return;
    
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const inviteCode = crypto.randomUUID().split('-')[0] + crypto.randomUUID().split('-')[0];
      
      const { error } = await supabase
        .from("group_invites")
        .insert({
          group_id: selectedGroupId,
          invite_code: inviteCode,
          created_by: user.id,
        });

      if (error) throw error;

      const link = `${window.location.origin}/join/${inviteCode}`;
      setInviteLink(link);
      sonnerToast.success("Invite link generated");
    } catch (error: any) {
      console.error("Error generating invite:", error);
      sonnerToast.error("Failed to generate invite link");
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      sonnerToast.success("Invite link copied to clipboard");
    }
  };

  const handleBeforeDelete = (lead: Lead, deletedData?: DeletedLeadData) => {
    setLastChange({ type: 'delete', lead, deletedData });
  };

  const handleBeforeUpdate = (lead: Lead) => {
    setLastChange({ type: 'update', lead });
  };

  const handleUndo = async () => {
    if (!lastChange) return;

    try {
      const user = await getCurrentUser();
      if (!user) return;

      if (lastChange.type === 'delete') {
        // Restore deleted lead
        const { error } = await supabase
          .from("leads")
          .insert([{
            id: lastChange.lead.id,
            user_id: lastChange.lead.user_id || user.id,
            lead_name: lastChange.lead.lead_name,
            company: lastChange.lead.company,
            email: lastChange.lead.email,
            phone: lastChange.lead.phone,
            status: lastChange.lead.status,
            priority: lastChange.lead.priority,
            value: lastChange.lead.value,
            source: lastChange.lead.source,
            conversation_notes: lastChange.lead.conversation_notes,
            next_action: lastChange.lead.next_action,
            next_action_date: lastChange.lead.next_action_date,
            custom_fields: lastChange.lead.custom_fields,
            last_refreshed_at: lastChange.lead.last_refreshed_at,
            group_id: lastChange.lead.group_id,
          }]);

        if (error) throw error;

        // Restore gantt items and re-sync to Google Calendar
        if (lastChange.deletedData?.ganttItems?.length) {
          await restoreGanttItemsWithCalendarSync(
            lastChange.deletedData.ganttItems,
            lastChange.lead.lead_name,
            {
              groupId: lastChange.lead.group_id || selectedGroupId || null,
              workspaceName: groupName || undefined,
            }
          );
        }

        toast.success("Lead restored", { description: "The deleted lead and its timeline events have been restored." });
      } else {
        // Restore previous state of updated lead
        const { error } = await supabase
          .from("leads")
          .update({
            lead_name: lastChange.lead.lead_name,
            company: lastChange.lead.company,
            email: lastChange.lead.email,
            phone: lastChange.lead.phone,
            status: lastChange.lead.status,
            priority: lastChange.lead.priority,
            value: lastChange.lead.value,
            source: lastChange.lead.source,
            conversation_notes: lastChange.lead.conversation_notes,
            next_action: lastChange.lead.next_action,
            next_action_date: lastChange.lead.next_action_date,
            custom_fields: lastChange.lead.custom_fields,
            last_refreshed_at: lastChange.lead.last_refreshed_at,
          })
          .eq("id", lastChange.lead.id);

        if (error) throw error;

        toast.success("Change reverted", { description: "The lead has been reverted to its previous state." });
      }

      setLastChange(null);
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const fetchRefresherLeads = async () => {
    const requestId = ++leadRequestIdRef.current;
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      if (workspace === "groups" && !selectedGroupId) {
        setLeads([]);
        setFilteredLeads([]);
        setLoading(false);
        return;
      }

      // Check if we're on a group URL but workspace hasn't updated yet
      const isGroupUrl = window.location.pathname.includes('/dashboard/groups/');
      if (isGroupUrl && workspace === "personal") {
        // Wait for workspace context to update
        setLoading(false);
        return;
      }

      // Fetch reminder days from appropriate source
      let globalDays = 30;
      if (workspace === "groups" && selectedGroupId) {
        const { data: groupData } = await requestWithAuthRecovery(() =>
          supabase
            .from("groups")
            .select("reminder_days")
            .eq("id", selectedGroupId)
            .single(),
        );
        globalDays = groupData?.reminder_days || 30;
      } else {
        const { data: profileData } = await requestWithAuthRecovery(() =>
          supabase
            .from("profiles")
            .select("reminder_days")
            .eq("user_id", user.id)
            .single(),
        );
        globalDays = profileData?.reminder_days || 30;
      }

      setReminderDays(globalDays);

      const requestLeads = () => {
        let query = supabase
          .from("leads")
          .select("*");

        if (workspace === "groups" && selectedGroupId) {
          query = query.eq("group_id", selectedGroupId);
        } else {
          query = query.eq("user_id", user.id).is("group_id", null);
        }

        return query;
      };

      const { data, error } = await requestWithAuthRecovery(requestLeads);

      if (error) throw error;
      if (requestId !== leadRequestIdRef.current) return;

      // Filter leads based on their custom or global reminder days
      // Show leads that need refreshing (past reminder window) BUT not manually moved to cold
      const now = new Date();
      const filteredData = (data || []).filter(lead => {
        // Exclude leads manually moved to cold (very old date)
        const lastRefreshDate = lead.last_refreshed_at 
          ? new Date(lead.last_refreshed_at) 
          : new Date(lead.created_at);
        
        if (lastRefreshDate < new Date('2000-01-01')) {
          console.log(`[Refresher Page] Excluding ${lead.lead_name}: manually moved to cold`);
          return false; // Manually moved to cold
        }
        
        const customFields = lead.custom_fields as Record<string, any> | null;
        const customDays = customFields?.custom_reminder_days;
        const daysToUse = customDays || globalDays;
        
        const thresholdDate = new Date();
        thresholdDate.setDate(thresholdDate.getDate() - daysToUse);
        
        const needsRefresh = lastRefreshDate <= thresholdDate;
        console.log(`[Refresher Page] ${lead.lead_name}: lastRefresh=${lastRefreshDate.toISOString()}, threshold=${thresholdDate.toISOString()}, needsRefresh=${needsRefresh}, daysToUse=${daysToUse}`);
        
        return needsRefresh;
      });

      setLeads(filteredData as Lead[] || []);
      console.log(`[Refresher] Found ${filteredData.length} leads that need refreshing:`, filteredData.map(l => ({ name: l.lead_name, id: l.id })));
    } catch (error: any) {
      console.error("Error fetching leads:", error);
      if (requestId === leadRequestIdRef.current) {
        toast.error("Error", { description: error.message });
      }
    } finally {
      if (requestId === leadRequestIdRef.current) setLoading(false);
    }
  };

  const handleRefresh = async (leadId: string) => {
    try {
      const customDays = customReminderDays[leadId];
      const updateData: any = { 
        last_refreshed_at: new Date().toISOString() 
      };

      // If user provided custom reminder days, validate and save them
      if (customDays && customDays.trim() !== "") {
        const daysNumber = parseInt(customDays);
        
        try {
          customReminderSchema.parse({ days: daysNumber });
          
          // Save custom reminder days in custom_fields
          const lead = leads.find(l => l.id === leadId);
          updateData.custom_fields = {
            ...lead?.custom_fields,
            custom_reminder_days: daysNumber
          };
        } catch (validationError) {
          toast.error("Invalid reminder days", { description: "Please enter a number between 1 and 365 days" });
          return;
        }
      }

      const { error } = await supabase
        .from("leads")
        .update(updateData)
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Lead refreshed", {
        description: customDays && customDays.trim() !== "" 
          ? `Timer reset with custom ${customDays} day interval`
          : "Timer has been reset.",
      });
      
      // Clear the custom input for this lead
      setCustomReminderDays(prev => {
        const newState = { ...prev };
        delete newState[leadId];
        return newState;
      });
      
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleMoveToCold = async (leadId: string) => {
    try {
      const oldDate = new Date('1900-01-01').toISOString();
      
      const { error } = await supabase
        .from("leads")
        .update({ 
          last_refreshed_at: oldDate,
          updated_at: new Date().toISOString()
        })
        .eq("id", leadId);

      if (error) throw error;

      toast.success("Lead moved to Cold", { description: "The lead has been moved to Cold Leads." });
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleBulkHot = async () => {
    if (selectedLeads.length === 0) return;
    try {
      const { error } = await supabase
        .from("leads")
        .update({ last_refreshed_at: new Date().toISOString() })
        .in("id", selectedLeads);

      if (error) throw error;

      toast.success("Leads refreshed", { description: `${selectedLeads.length} lead(s) marked as Hot.` });
      setSelectedLeads([]);
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleBulkCold = async () => {
    if (selectedLeads.length === 0) return;
    try {
      const oldDate = new Date('1900-01-01').toISOString();
      const { error } = await supabase
        .from("leads")
        .update({ last_refreshed_at: oldDate, updated_at: new Date().toISOString() })
        .in("id", selectedLeads);

      if (error) throw error;

      toast.success("Leads moved to Cold", { description: `${selectedLeads.length} lead(s) moved to Cold.` });
      setSelectedLeads([]);
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleBulkCustomTimer = async () => {
    if (selectedLeads.length === 0) return;
    const daysNumber = parseInt(bulkCustomDays);
    try {
      customReminderSchema.parse({ days: daysNumber });
    } catch {
      toast.error("Invalid days", { description: "Enter a number between 1 and 365." });
      return;
    }

    try {
      // Update each lead's custom_fields with the custom timer, then refresh
      for (const leadId of selectedLeads) {
        const lead = leads.find(l => l.id === leadId);
        const { error } = await supabase
          .from("leads")
          .update({
            last_refreshed_at: new Date().toISOString(),
            custom_fields: {
              ...lead?.custom_fields,
              custom_reminder_days: daysNumber
            }
          })
          .eq("id", leadId);
        if (error) throw error;
      }

      toast.success("Custom timer set", { description: `${selectedLeads.length} lead(s) refreshed with ${daysNumber}-day timer.` });
      setSelectedLeads([]);
      setBulkCustomDays("");
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;
    try {
      await deleteLeadsWithCalendarCleanup(selectedLeads, {
        groupId: selectedGroupId || null,
        workspaceName: groupName || undefined,
      });

      toast.success("Leads deleted", { description: `${selectedLeads.length} lead(s) deleted.` });
      setSelectedLeads([]);
      fetchRefresherLeads();
    } catch (error: any) {
      toast.error("Error", { description: error.message });
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="p-4 md:p-8 space-y-6">
      {isGroupSwitching ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Switching groups...</p>
        </div>
      ) : workspace === "groups" && !selectedGroupId ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">No Group Selected</h2>
            <p className="text-muted-foreground">Create or join a group to get started</p>
          </div>
          <Button onClick={() => setCreateGroupDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Your First Group
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <h1 className="text-2xl md:text-3xl font-bold">Refresher</h1>
            {workspace === "groups" && groupName && (
              <h2 className="text-2xl md:text-4xl font-bold md:flex-1 text-center">{groupName}</h2>
            )}
            <div className="flex flex-wrap gap-2 items-center">
          <LeadCountBadge />
          {workspace === "groups" && selectedGroupId && (
            <Button
              variant="outline"
              onClick={() => setSettingsDialogOpen(true)}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden md:inline">Group Settings</span>
            </Button>
          )}
          {workspace === "groups" && selectedGroupId && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Link2 className="h-4 w-4" />
                  <span className="hidden md:inline">Invite Link</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium mb-1">Invite Members</h4>
                    <p className="text-sm text-muted-foreground">
                      Share this link to invite people to your group
                    </p>
                  </div>
                  {inviteLink ? (
                    <div className="space-y-2">
                      <Label className="text-xs">Invite Link</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={inviteLink} 
                          readOnly 
                          className="text-xs"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={copyInviteLink}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={generateInviteLink}
                      className="w-full gap-2"
                      size="sm"
                    >
                      <Link2 className="h-4 w-4" />
                      Generate Link
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Button onClick={() => setAddDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Lead</span>
          </Button>
        </div>
      </div>

      <LeadFilters
        leads={leads}
        columnConfig={columnConfig}
        onFilterChange={setFilteredLeads}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={(field, direction) => {
          setSortField(field);
          setSortDirection(direction);
        }}
      />
      
      <div className="flex flex-wrap justify-start gap-2 mb-4">
        <Button 
          className="gap-2"
          onClick={() => exportLeadsToCSV(filteredLeads, columnConfig, 'refresher-leads', false, reminderDays)}
          disabled={filteredLeads.length === 0}
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Generate Report</span>
        </Button>
        <Button 
          variant="outline"
          className="gap-2"
          onClick={handleUndo}
          disabled={!lastChange}
        >
          <Undo className="h-4 w-4" />
          <span className="hidden sm:inline">Undo {lastChange?.type === 'delete' ? 'Delete' : 'Edit'}</span>
        </Button>
        <Button 
          variant="outline"
          onClick={() => setVersionHistoryOpen(true)}
          className="gap-2"
        >
          <History className="h-4 w-4" />
          <span className="hidden sm:inline">Version History</span>
        </Button>

        {/* Sheet-level Google Calendar sync */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline"
              className="gap-2"
              disabled={isCalendarLoading || isCalendarSyncing}
            >
              {isCalendarSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : !canAccessCalendarSync ? (
                <Lock className="h-4 w-4" />
              ) : (
                <CalendarSync className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isCalendarSyncing ? "Syncing..." : "Sync Calendar"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!canAccessCalendarSync ? (
              <div className="p-3 text-center max-w-[200px]">
                <Lock className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Calendar Sync Locked</p>
                <p className="text-xs text-muted-foreground mb-2">Requires an active subscription</p>
                <Button size="sm" className="w-full" onClick={() => window.location.href = "/pricing"}>Upgrade</Button>
              </div>
            ) : isCalendarConnected ? (
              <>
                <DropdownMenuItem onClick={handleSyncAllLeads} disabled={leads.length === 0 || isCalendarSyncing}>
                  <CalendarSync className="h-4 w-4 mr-2" />
                  Sync All Lead Timelines
                </DropdownMenuItem>
                <DropdownMenuItem onClick={disconnectCalendar} className="text-destructive">
                  <X className="h-4 w-4 mr-2" />
                  Disconnect Calendar
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem onClick={connectCalendar}>
                <Calendar className="h-4 w-4 mr-2" />
                Connect Google Calendar
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selectedLeads.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-muted/50 mb-4">
          <span className="text-sm font-medium mr-1">
            <CheckSquare className="h-4 w-4 inline mr-1" />
            {selectedLeads.length} selected
          </span>
          <Button size="sm" onClick={handleBulkHot} className="h-7 px-3 text-xs">
            Hot
          </Button>
          <Button size="sm" variant="outline" onClick={handleBulkCold} className="h-7 px-3 text-xs">
            Cold
          </Button>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="1"
              max="365"
              placeholder="Days"
              value={bulkCustomDays}
              onChange={(e) => setBulkCustomDays(e.target.value)}
              className="w-16 h-7 text-xs"
            />
            <Button size="sm" variant="outline" onClick={handleBulkCustomTimer} className="h-7 px-3 text-xs whitespace-nowrap">
              <Clock className="h-3 w-3 mr-1" />
              Set Timer
            </Button>
          </div>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="h-7 px-3 text-xs">
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedLeads([])} className="h-7 px-3 text-xs">
            Clear
          </Button>
        </div>
      )}

      {filteredLeads.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-card">
          <p className="text-muted-foreground mb-4">
            No leads need follow-up right now. Check back after {reminderDays} days of inactivity!
          </p>
        </div>
      ) : (
        <LeadsTable
          leads={filteredLeads}
          onLeadsChange={fetchRefresherLeads}
          columnConfig={columnConfig}
          onBeforeDelete={handleBeforeDelete}
          onBeforeUpdate={handleBeforeUpdate}
          sortField={sortField}
          sortDirection={sortDirection}
          showReminderColumn={false}
          selectedLeads={selectedLeads}
          onSelectedLeadsChange={setSelectedLeads}
          externalOpenLeadId={outletContext?.openLeadDetails}
          onExternalOpenLeadClose={() => outletContext?.setOpenLeadDetails(null)}
          highlightedLeadId={outletContext?.highlightedLeadId}
          customActions={(lead) => (
            <div className="flex items-center gap-1.5 sticky right-0 bg-background pl-3 border-l">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor={`custom-days-${lead.id}`} className="text-[10px] text-muted-foreground leading-none">
                  Custom Timer
                </Label>
                <Input
                  id={`custom-days-${lead.id}`}
                  type="number"
                  min="1"
                  max="365"
                  placeholder={lead.custom_fields?.custom_reminder_days || reminderDays.toString()}
                  value={customReminderDays[lead.id] || ""}
                  onChange={(e) => setCustomReminderDays(prev => ({
                    ...prev,
                    [lead.id]: e.target.value
                  }))}
                  className="w-16 h-7 text-xs"
                />
              </div>
              <div className="flex gap-1">
                <Button onClick={() => handleRefresh(lead.id)} size="sm" className="whitespace-nowrap h-7 px-3 text-xs">
                  Hot
                </Button>
                <Button variant="outline" onClick={() => handleMoveToCold(lead.id)} size="sm" className="whitespace-nowrap h-7 px-3 text-xs">
                  Cold
                </Button>
              </div>
            </div>
          )}
        />
      )}

      <AddLeadDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onLeadAdded={fetchRefresherLeads}
        groupId={workspace === "groups" ? selectedGroupId : null}
      />

      {workspace === "groups" && selectedGroupId && (
        <GroupSettingsDialog
          open={settingsDialogOpen}
          onOpenChange={setSettingsDialogOpen}
          groupId={selectedGroupId}
          groupName={groupName}
          onGroupUpdated={fetchGroupInfo}
        />
      )}

      <VersionHistoryDialog
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        onRestore={fetchRefresherLeads}
      />
        </>
      )}

      <CreateGroupDialog
        open={createGroupDialogOpen}
        onOpenChange={setCreateGroupDialogOpen}
        onGroupCreated={(groupId) => {
          navigate(`/dashboard/groups/${groupId}/refresher`);
        }}
      />
    </div>
  );
};

export default Refresher;
